import { useMemo, useRef, useState } from 'react';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Link } from 'react-router-dom';
import { useContent } from '../App.jsx';
import { ArrowIcon, CheckIcon, PlusIcon, TrashIcon } from '../components/Icons.jsx';
import { calculatePricing } from '../lib/pricing.js';

function money(value) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function newAttendee(content) {
  return {
    id: crypto.randomUUID(),
    fullName: '',
    instrument: content.registration.instruments[0],
    level: content.registration.levels[0],
    shirt: content.registration.shirts[0]
  };
}

function AttendeeEditor({ attendee, index, content, onChange, onRemove, removable }) {
  const update = (field, value) => onChange({ ...attendee, [field]: value });
  return (
    <article className="attendee-editor">
      <div className="attendee-editor-heading"><div><span>Attendee {index + 1}</span><strong>{attendee.fullName || 'New attendee'}</strong></div>{removable && <button type="button" onClick={onRemove} aria-label={`Remove attendee ${index + 1}`}><TrashIcon /></button>}</div>
      <div className="form-grid">
        <label className="field field-wide"><span>Full name</span><input value={attendee.fullName} onChange={(event) => update('fullName', event.target.value)} autoComplete="name" placeholder="First and last name" /></label>
        <label className="field"><span>Instrument / track</span><select value={attendee.instrument} onChange={(event) => update('instrument', event.target.value)}>{content.registration.instruments.map((item) => <option key={item}>{item}</option>)}</select></label>
        <label className="field"><span>Experience level</span><select value={attendee.level} onChange={(event) => update('level', event.target.value)}>{content.registration.levels.map((item) => <option key={item}>{item}</option>)}</select></label>
        <label className="field field-wide"><span>Workshop shirt</span><select value={attendee.shirt} onChange={(event) => update('shirt', event.target.value)}>{content.registration.shirts.map((item) => <option key={item} value={item}>{item}{item === 'No Shirt' ? '' : ` (+${money(content.pricing.shirtPrice)})`}</option>)}</select></label>
      </div>
    </article>
  );
}

function StripeCheckout({ stripePromise, clientSecret, onComplete, onError, loading, setLoading }) {
  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: '#b85d31', borderRadius: '4px' } } }}>
      <StripeCheckoutForm onComplete={onComplete} onError={onError} loading={loading} setLoading={setLoading} />
    </Elements>
  );
}

function StripeCheckoutForm({ onComplete, onError, loading, setLoading }) {
  const stripe = useStripe();
  const elements = useElements();
  const submit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    const result = await stripe.confirmPayment({ elements, redirect: 'if_required' });
    if (result.error) {
      onError(result.error.message);
      setLoading(false);
    } else if (result.paymentIntent) {
      await onComplete(result.paymentIntent.id);
    }
  };
  return <form className="stripe-form" onSubmit={submit}><PaymentElement /><button className="button checkout-button" disabled={!stripe || loading}>{loading ? 'Confirming…' : 'Pay securely'}<ArrowIcon /></button></form>;
}

export default function RegisterPage() {
  const { content } = useContent();
  const [attendees, setAttendees] = useState(() => [newAttendee(content)]);
  const [contact, setContact] = useState({ fullName: '', email: '', phone: '' });
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationId, setRegistrationId] = useState('');
  const [emailStatus, setEmailStatus] = useState('');
  const [stripeSession, setStripeSession] = useState(null);
  const [donationAmount, setDonationAmount] = useState(0);
  const paypalSession = useRef(null);
  const pricing = useMemo(() => calculatePricing(attendees, content, new Date(), donationAmount), [attendees, content, donationAmount]);
  const provider = content.payments.provider;

  const updateAttendee = (index, attendee) => setAttendees((current) => current.map((item, itemIndex) => itemIndex === index ? attendee : item));
  const attendeesValid = attendees.every((attendee) => attendee.fullName.trim());
  const contactValid = contact.fullName.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email) && contact.phone.trim();
  const goToStep = (nextStep) => {
    setStep(nextStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const createOrder = async (paymentProvider = provider) => {
    setError('');
    const response = await fetch('/api/payments/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attendees, contact, donationAmount, provider: paymentProvider })
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Payment could not be started.');
    return result;
  };

  const captureOrder = async (orderId, paymentDetails = {}) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/payments/capture', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, ...paymentDetails })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Registration could not be completed.');
      setRegistrationId(result.registrationId);
      setEmailStatus(result.emailStatus || '');
      goToStep(3);
    } catch (captureError) {
      setError(captureError.message);
    } finally {
      setLoading(false);
    }
  };

  const completeMockPayment = async () => {
    setLoading(true);
    try {
      const result = await createOrder(provider);
      await captureOrder(result.orderId);
    } catch (paymentError) {
      setError(paymentError.message);
      setLoading(false);
    }
  };

  const prepareStripe = async () => {
    setLoading(true);
    try {
      const result = await createOrder('stripe');
      setStripeSession(result);
    } catch (paymentError) {
      setError(paymentError.message);
    } finally {
      setLoading(false);
    }
  };

  if (!content.event.registrationOpen) {
    return <section className="registration-closed"><p className="eyebrow">Registration</p><h1>Registration is currently closed.</h1><p>Questions? Reach out and we will be happy to help.</p><Link className="button button-dark" to="/contact">Contact us</Link></section>;
  }

  if (step === 3) {
    return (
      <section className="registration-success">
        <div className="success-mark"><CheckIcon /></div>
        <p className="eyebrow">Registration complete</p>
        <h1>You're all set.</h1>
        <p>Thanks for registering for the {content.event.year} Pocono Workshop. Your registration for <strong>{contact.email}</strong> has been recorded.{emailStatus === 'sent' ? ' A confirmation email is on its way.' : ''}</p>
        <div className="confirmation-number"><span>Confirmation</span><strong>{registrationId}</strong></div>
        <p className="success-note">Don't forget to reserve your hotel room while the workshop block is available.</p>
        <div className="success-actions"><Link className="button button-dark" to="/location">View hotel details</Link><Link className="text-link" to="/">Back to the workshop <ArrowIcon /></Link></div>
      </section>
    );
  }

  return (
    <div className="registration-page">
      <header className="registration-heading page-width">
        <p className="eyebrow">{content.event.displayDate}</p>
        <h1>Register for the workshop.</h1>
        <div className="stepper" aria-label={`Step ${step} of 2`}><div className={step >= 1 ? 'active' : ''}><span>1</span><p>Attendees</p></div><i /><div className={step >= 2 ? 'active' : ''}><span>2</span><p>Contact & payment</p></div></div>
      </header>

      <div className="registration-layout page-width">
        <section className="registration-form-area">
          {step === 1 && (
            <>
              <div className="form-section-heading"><div><p className="eyebrow">Who's coming?</p><h2>Attendee information</h2></div><p>Add everyone in your registration. Group pricing is applied automatically at {content.pricing.groupMinimum} attendees.</p></div>
              <div className="attendee-list">
                {attendees.map((attendee, index) => <AttendeeEditor key={attendee.id} attendee={attendee} index={index} content={content} onChange={(value) => updateAttendee(index, value)} onRemove={() => setAttendees((current) => current.filter((_, itemIndex) => itemIndex !== index))} removable={attendees.length > 1} />)}
              </div>
              <button className="add-attendee" type="button" onClick={() => setAttendees((current) => [...current, newAttendee(content)])}><PlusIcon /> Add another attendee</button>
              {!attendeesValid && error && <p className="form-error">Enter a name for every attendee.</p>}
              <div className="form-navigation"><span /><button className="button button-dark" type="button" onClick={() => { if (!attendeesValid) return setError('Add each attendee’s name before continuing.'); setError(''); goToStep(2); }}>Continue to checkout <ArrowIcon /></button></div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="form-section-heading"><div><p className="eyebrow">Nearly there</p><h2>Contact & payment</h2></div><p>Your confirmation and workshop updates will be sent to this contact.</p></div>
              <article className="contact-editor">
                <div className="form-grid">
                  <label className="field field-wide"><span>Contact name</span><input value={contact.fullName} onChange={(event) => setContact({ ...contact, fullName: event.target.value })} autoComplete="name" /></label>
                  <label className="field"><span>Email address</span><input type="email" value={contact.email} onChange={(event) => setContact({ ...contact, email: event.target.value })} autoComplete="email" /></label>
                  <label className="field"><span>Phone number</span><input type="tel" value={contact.phone} onChange={(event) => setContact({ ...contact, phone: event.target.value })} autoComplete="tel" /></label>
                </div>
              </article>
              {content.registration.donationEnabled && (
                <details className="donation-option">
                  <summary>
                    <span><small>Optional contribution</small><strong>{content.registration.donationTitle}</strong></span>
                    <span className="donation-summary-action">{donationAmount > 0 ? `+$${donationAmount.toFixed(2)}` : 'Add if you’d like'} <PlusIcon /></span>
                  </summary>
                  <div className="donation-content">
                    <p>{content.registration.donationBody}</p>
                    <div className="donation-choices" role="group" aria-label="Optional contribution amount">
                      <button type="button" className={donationAmount === 0 ? 'active' : ''} onClick={() => setDonationAmount(0)}>No contribution</button>
                      {content.registration.donationOptions.map((amount) => <button type="button" className={donationAmount === amount ? 'active' : ''} key={amount} onClick={() => setDonationAmount(Number(amount))}>${amount}</button>)}
                      <label><span>Other $</span><input type="number" min="0" max="5000" step="1" aria-label="Custom contribution amount" value={content.registration.donationOptions.includes(donationAmount) || donationAmount === 0 ? '' : donationAmount} onChange={(event) => setDonationAmount(Math.min(5000, Math.max(0, Number(event.target.value) || 0)))} /></label>
                    </div>
                  </div>
                </details>
              )}
              <div className="payment-panel">
                <div className="payment-panel-heading"><div><p className="eyebrow">Secure checkout</p><h3>{provider === 'stripe' ? 'Pay by card' : 'Pay with PayPal'}</h3></div><span>{money(pricing.total)}</span></div>
                {!contactValid && <p className="payment-hint">Complete your contact information to enable payment.</p>}
                {content.payments.mock && <div className="demo-payment"><p><strong>Local review mode</strong><br />No real payment will be taken. This creates a sample registration in the local admin.</p><button className="button checkout-button" type="button" disabled={!contactValid || loading} onClick={completeMockPayment}>{loading ? 'Completing…' : 'Complete demo registration'} <ArrowIcon /></button></div>}
                {!content.payments.mock && provider === 'paypal' && content.payments.paypalConfigured && contactValid && (
                  <PayPalScriptProvider options={{ clientId: content.payments.paypalClientId, currency: 'USD', intent: 'capture', components: 'buttons' }}>
                    <PayPalButtons
                      style={{ layout: 'vertical', shape: 'rect', label: 'paypal' }}
                      disabled={loading}
                      createOrder={async (_data, actions) => {
                        const session = await createOrder('paypal');
                        paypalSession.current = session;
                        if (!session.clientCapture) return session.orderId;
                        return actions.order.create({
                          purchase_units: [{ reference_id: session.reference, amount: { currency_code: 'USD', value: Number(session.amount).toFixed(2) } }],
                          application_context: { shipping_preference: 'NO_SHIPPING', user_action: 'PAY_NOW' }
                        });
                      }}
                      onApprove={async ({ orderID }, actions) => {
                        const session = paypalSession.current;
                        if (session?.clientCapture) {
                          const paypalCapture = await actions.order.capture();
                          await captureOrder(orderID, { checkoutSessionId: session.orderId, paypalCapture });
                        } else {
                          await captureOrder(orderID);
                        }
                      }}
                      onError={() => { setLoading(false); setError('PayPal could not complete the payment. Please try again.'); }}
                    />
                  </PayPalScriptProvider>
                )}
                {!content.payments.mock && provider === 'paypal' && !content.payments.paypalConfigured && <p className="form-error">PayPal is not configured yet.</p>}
                {!content.payments.mock && provider === 'stripe' && content.payments.stripeEnabled && contactValid && !stripeSession && <button className="button checkout-button" type="button" disabled={loading} onClick={prepareStripe}>{loading ? 'Preparing…' : 'Continue to card payment'} <ArrowIcon /></button>}
                {!content.payments.mock && provider === 'stripe' && !content.payments.stripeEnabled && <p className="form-error">Card checkout is not configured yet.</p>}
                {!content.payments.mock && stripeSession && <StripeCheckout stripePromise={loadStripe(content.payments.stripePublishableKey)} clientSecret={stripeSession.clientSecret} onComplete={captureOrder} onError={setError} loading={loading} setLoading={setLoading} />}
                <p className="payment-security">Payment details are handled by {provider === 'stripe' ? 'Stripe' : 'PayPal'} and never stored on this site.</p>
              </div>
              {error && <p className="form-error" role="alert">{error}</p>}
              <div className="form-navigation"><button className="text-button" type="button" onClick={() => goToStep(1)}>← Back to attendees</button><span /></div>
            </>
          )}
        </section>

        <aside className="order-summary">
          <p className="eyebrow">Your registration</p>
          <div className="summary-attendees">{attendees.map((attendee, index) => <div key={attendee.id}><span>{attendee.fullName || `Attendee ${index + 1}`}<small>{attendee.instrument} · {attendee.level}</small></span><strong>{money(pricing.unitPrice + (attendee.shirt === 'No Shirt' ? 0 : content.pricing.shirtPrice))}</strong></div>)}</div>
          {pricing.group && <p className="discount-note"><CheckIcon /> Group pricing applied</p>}
          <div className="summary-totals"><div><span>Subtotal</span><strong>{money(pricing.subtotal)}</strong></div><div><span>Processing fee ({content.pricing.processingFeePercent}%)</span><strong>{money(pricing.processingFee)}</strong></div>{pricing.donationAmount > 0 && <div className="summary-donation"><span>Optional contribution</span><strong>{money(pricing.donationAmount)}</strong></div>}<div className="summary-total"><span>Total</span><strong>{money(pricing.total)}</strong></div></div>
          <p className="refund-note">Refunds are unavailable after October 31, {content.event.year}.</p>
        </aside>
      </div>
    </div>
  );
}
